/**
 * Pagination Controller Module
 * Handles table pagination across the application
 *
 * Following OOA/OOD principles:
 * - Single Responsibility: Only pagination logic
 * - Dependency Injection: Table renderers are injected
 * - Open/Closed: Extensible for new table types
 */

/**
 * Table renderer callback type
 */
export type TableRenderer = (data: any[], updateRawData: boolean) => void;

/**
 * Settings service interface
 */
export interface SettingsService {
    getDataDisplayRows(): number;
    setDataDisplayRows(rows: number): void;
    onChange(setting: string, callback: (value: any) => void): void;
}

/**
 * Pagination state for a single table
 */
interface TablePaginationState {
    service: any;
    renderer: TableRenderer;
    isFiltering: boolean;
    filteredData: any[];
    rawData: any[];
}

/**
 * PaginationController class
 * Manages pagination for multiple tables
 */
export class PaginationController {
    private tables: Map<string, TablePaginationState>;
    private settingsService: SettingsService;

    constructor(settingsService: SettingsService) {
        this.tables = new Map();
        this.settingsService = settingsService;
    }

    /**
     * Initialize pagination for all registered tables
     */
    public initialize(): void {
        if (!(window as any).PaginationService) {
            console.warn('PaginationService not available');
            return;
        }

        const pageSize = this.settingsService.getDataDisplayRows();

        // Initialize services for each registered table
        this.tables.forEach((state, tableName) => {
            state.service = new (window as any).PaginationService(`${tableName}-table`, { pageSize });
            this.setupPaginationHandlers(tableName);
        });

        // Setup settings change listener
        this.settingsService.onChange('dataDisplayRows', (newValue: number) => {
            console.log(`Data display rows changed to ${newValue}`);
            this.tables.forEach((state) => {
                if (state.service) {
                    state.service.setPageSize(newValue);
                }
            });
        });

        // Setup Data Display setting dropdown
        const dataDisplaySelect = document.getElementById('data-display-rows') as HTMLSelectElement;
        if (dataDisplaySelect) {
            dataDisplaySelect.value = pageSize.toString();
            dataDisplaySelect.addEventListener('change', (e: Event) => {
                const target = e.target as HTMLSelectElement;
                const newValue = parseInt(target.value, 10);
                this.settingsService.setDataDisplayRows(newValue);
            });
        }

        console.log('Pagination services initialized');
    }

    /**
     * Register a table for pagination
     */
    public registerTable(tableName: string, renderer: TableRenderer): void {
        this.tables.set(tableName, {
            service: null,
            renderer,
            isFiltering: false,
            filteredData: [],
            rawData: []
        });
    }

    /**
     * Get pagination service for a table
     */
    public getService(tableName: string): any {
        return this.tables.get(tableName)?.service;
    }

    /**
     * Set filtering state for a table
     */
    public setFiltering(tableName: string, isFiltering: boolean, filteredData: any[] = []): void {
        const state = this.tables.get(tableName);
        if (state) {
            state.isFiltering = isFiltering;
            state.filteredData = filteredData;
        }
    }

    /**
     * Set raw data for a table
     */
    public setRawData(tableName: string, data: any[]): void {
        const state = this.tables.get(tableName);
        if (state) {
            state.rawData = data;
        }
    }

    /**
     * Get raw data for a table
     */
    public getRawData(tableName: string): any[] {
        return this.tables.get(tableName)?.rawData || [];
    }

    /**
     * Check if table is in filtering mode
     */
    public isFiltering(tableName: string): boolean {
        return this.tables.get(tableName)?.isFiltering || false;
    }

    /**
     * Setup pagination button handlers for a table
     */
    private setupPaginationHandlers(tableName: string): void {
        const prevButton = document.getElementById(`${tableName}-prev-page`);
        if (prevButton) {
            prevButton.addEventListener('click', () => this.handlePreviousPage(tableName));
        }

        const nextButton = document.getElementById(`${tableName}-next-page`);
        if (nextButton) {
            nextButton.addEventListener('click', () => this.handleNextPage(tableName));
        }
    }

    /**
     * Handle next page navigation
     */
    public async handleNextPage(tableName: string): Promise<void> {
        const state = this.tables.get(tableName);
        if (!state?.service || !state.service.nextPage()) {
            return;
        }

        if (state.isFiltering) {
            this.displayFilteredPage(tableName);
        } else {
            await this.loadPageData(tableName);
        }
    }

    /**
     * Handle previous page navigation
     */
    public async handlePreviousPage(tableName: string): Promise<void> {
        const state = this.tables.get(tableName);
        if (!state?.service || !state.service.previousPage()) {
            return;
        }

        if (state.isFiltering) {
            this.displayFilteredPage(tableName);
        } else {
            await this.loadPageData(tableName);
        }
    }

    /**
     * Display a page of filtered data
     */
    public displayFilteredPage(tableName: string): void {
        const state = this.tables.get(tableName);
        if (!state?.service || !state.filteredData) {
            console.warn(`Cannot display filtered page for ${tableName}`);
            return;
        }

        const offset = state.service.getOffset();
        const limit = state.service.getLimit();
        const pageData = state.filteredData.slice(offset, offset + limit);

        console.log(`Displaying filtered page ${state.service.getCurrentPage()}: ${pageData.length} items`);

        // Use the registered renderer
        state.renderer(pageData, false);

        this.updatePaginationInfo(tableName, state.service, state.isFiltering);
        this.updatePaginationButtons(tableName, state.service);
    }

    /**
     * Load page data from server
     * Sprint 36: Uses AgentAdapter via queryCanister
     *
     * Maps table names to vector_db canister methods:
     * - devices → get_all_devices
     * - applications → get_all_applications
     * - relationships → get_all_relationships
     */
    public async loadPageData(tableName: string): Promise<void> {
        const state = this.tables.get(tableName);
        if (!state?.service) {
            console.warn(`No pagination service for ${tableName}`);
            return;
        }

        const offset = state.service.getOffset();
        const limit = state.service.getLimit();

        console.log(`Loading ${tableName} page: offset=${offset}, limit=${limit}`);

        // Map table names to canister methods
        const methodMap: Record<string, string> = {
            'devices': 'get_all_devices',
            'applications': 'get_all_applications',
            'relationships': 'get_all_relationships',
        };

        const methodName = methodMap[tableName];
        if (!methodName) {
            console.error(`No canister method mapped for table: ${tableName}`);
            return;
        }

        try {
            const { queryCanister } = await import('../../api/communication');
            const result = await queryCanister<any[]>('vector_db', methodName, []);

            const allData = Array.isArray(result) ? result : [];
            state.service.setTotalItems(allData.length);
            this.updatePaginationInfo(tableName, state.service, state.isFiltering);
            this.updatePaginationButtons(tableName, state.service);

            // Slice for current page
            const pageData = allData.slice(offset, offset + limit);

            // Transform data if needed and render
            let data = pageData;
            if (tableName === 'devices') {
                data = pageData.map(([deviceId, deviceData]: [string, any]) => ({ ...deviceData, id: deviceId }));
            }
            state.renderer(data, false);

            console.log(`Loaded page ${state.service.getCurrentPage()} of ${state.service.getTotalPages()}`);
        } catch (error) {
            console.error(`Error loading ${tableName} data:`, error);
        }
    }

    /**
     * Update pagination info display
     */
    public updatePaginationInfo(tableName: string, pagination: any, isFiltering: boolean = false): void {
        const infoElement = document.getElementById(`${tableName}-pagination-info`);
        if (infoElement) {
            let displayText = pagination.getDisplayString();
            if (isFiltering) {
                displayText += ' (filtered)';
            }
            infoElement.textContent = displayText;
        }
    }

    /**
     * Update pagination button states
     */
    public updatePaginationButtons(tableName: string, pagination: any): void {
        const prevButton = document.getElementById(`${tableName}-prev-page`) as HTMLButtonElement;
        const nextButton = document.getElementById(`${tableName}-next-page`) as HTMLButtonElement;

        if (prevButton) {
            prevButton.disabled = !pagination.hasPrevious();
        }

        if (nextButton) {
            nextButton.disabled = !pagination.hasNext();
        }
    }

    /**
     * Refresh pagination display for a table
     */
    public refresh(tableName: string): void {
        const state = this.tables.get(tableName);
        if (state?.service) {
            this.updatePaginationInfo(tableName, state.service, state.isFiltering);
            this.updatePaginationButtons(tableName, state.service);
        }
    }
}
